import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  IconButton,
  Icon,
  TableSortLabel,
  Badge,
  Tooltip,
  Collapse,
  Popper,
  Grow,
  ClickAwayListener,
  MenuList,
  MenuItem,
  ListItemIcon,
  Typography,
  Checkbox,
} from "@material-ui/core";
import { ArrowDropDown } from "@material-ui/icons";
import { observer } from "mobx-react-lite";

import React, { CSSProperties, FC, useEffect, useState } from "react";
import { Formater, TypeRecords, TYPE_ALIGN } from "src/framework/commons";
import { IHttpClient } from "src/framework/providers/api";
import { DataGridStore, Filter, ResourcePaths } from "src/framework/store/DataGridStore";

import { FlatButton } from "../../buttons";
import { BottomBar } from "../../commons";
import { InputText, InputTextParameters } from "../../forms";
import { ExpandableRow } from "./ExpandableRow";
import { Row } from "./Row";
import "./style.css";

export enum SUFIXO_FIELD {
  LIKE = "_like",
  NOT_LIKE = "_not_like",
  MORE = "_more",
  MORE_EQUAL = "_more_equal",
  LESS = "_less",
  LESS_EQUAL = "_more_less",
  IS_NULL = "_is_null",
  IS_NOT_NULL = "_is_not_null",
  IN = "_in",
  NOT_IN = "_not_in",
  NOT_EQUAL = "_not_equal",
}

export interface GroupColumnDefinition {
  headerName: string;
  colSpan: number;
}

export interface ColumnDefinition {
  group?: string;
  field: string;
  headerName: string;
  width?: number;
  description?: string;
  hide?: boolean;
  sortable?: boolean;
  mask?: string;
  type?: TypeRecords;
  align?: TYPE_ALIGN;
  render?: (row: any, style?: CSSProperties) => React.ReactNode;
}

interface changeTab {
  (row: any): void;
}

export interface Tab {
  id: string;
  title: React.ReactNode;
  subtitle?: string;
  content?: React.ReactNode;
}

export interface DataGridProperties {
  title: string;
  provider: IHttpClient;
  columnDefinitions: ColumnDefinition[];
  list?: any;
  path: string | ResourcePaths;
  width: string;
  height: string;
  maxHeight?: string;
  onChangeTab?: changeTab;
  filter?: Filter[];
  expandableRows?: boolean;
  renderExpandable?: (row: any) => React.ReactNode | null;
  enableSearch?: boolean;
  searchFields?: string[];

  // Checked Controls
  checkable?: boolean;
  onCheckAll?: <R = void>() => R;
  checkField?: string;
  renderCheckedActions?: () => React.ReactNode;
  renderAggregation?: (aggregates: any) => React.ReactNode;
  renderGrouping?: (row: any, index: number, list: any[]) => React.ReactNode;

  disablefilterDefault?: boolean;
  filters?: React.ReactNode;
  advancedFilters?: React.ReactNode;
  store: DataGridStore;
}

const DataGrid: FC<DataGridProperties> = observer(
  (props: DataGridProperties) => {
    const [dataGridStore, seDataGridStore] = useState<DataGridStore>(props.store);

    const [defaultFilter, setDefaultFilter] = useState<string>(props.filter ? props.filter[0].value : "");

    const [open, setOpen] = React.useState(false);
    const [openAgg, setOpenAgg] = React.useState(props?.renderAggregation !== undefined);
    const anchorRef = React.useRef<HTMLButtonElement>(null);


    useEffect(() => {
      seDataGridStore(props.store)
      dataGridStore.setPath(props.path);
      dataGridStore.setRows([]);
      dataGridStore.setPage(1);
    }, [props.store]);



    const handleToggle = () => {
      setOpen((prevOpen) => !prevOpen);
    };
    const handleClose = (event: React.MouseEvent<EventTarget>) => {
      if (
        anchorRef.current &&
        anchorRef.current.contains(event.target as HTMLElement)
      ) {
        return;
      }

      setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
      if (event.key === "Tab") {
        event.preventDefault();
        setOpen(false);
      }
    }

    const handleReport = (event: React.MouseEvent<EventTarget>) => {
      try {
        dataGridStore.setEnableExport(false);
        dataGridStore.export();
      } finally {
        dataGridStore.setEnableExport(true);
        handleClose(event);
      }
    };

    // return focus to the button when we transitioned from !open -> open
    const prevOpen = React.useRef(open);
    React.useEffect(() => {
      if (prevOpen.current === true && open === false) {
        anchorRef.current!.focus();
      }

      prevOpen.current = open;
    }, [open]);

    let lastGroup: string | undefined = undefined;
    let lastGroupContent: string | undefined = undefined;

    let { enableSearch, searchFields } = props;

    enableSearch =
      (!!searchFields && enableSearch === undefined) || enableSearch === true
        ? true
        : false;




    // useEffect(() => {
    //   dataGridStore.setRows([]);
    //   dataGridStore.list();
    //   console.log(`Grid ${props.title} is rebuilded`);
    // }, [dataGridStore.page, dataGridStore.pageSize, dataGridStore.plainFilter]);

    /* Controla a posição inicial da grade, o grupo influencia nisso */
    const isGroupHeader = dataGridStore.columnDefinitions.some(
      (column: ColumnDefinition) => column.group
    );

    const handleChangePage = (newPage: number) => {
      dataGridStore.setPage(newPage + 1);
      dataGridStore.setRows([]);
      dataGridStore.list();
    };

    const handleChangeRowsPerPage = (value: any) => {
      dataGridStore.setRows([]);
      dataGridStore.setPageSize(parseInt(value));
      dataGridStore.setPage(1);
      dataGridStore.list();
    };

    const isOrderActive = (
      column: string,
      active: boolean = false
    ): boolean => {
      if (!active) {
        return active;
      }
      const idx = dataGridStore.orderBy.findIndex((field: string) =>
        field.match(new RegExp(`${column}|${column}-desc`))
      );

      return idx > -1;
    };

    const orderPriority = (column: string, active: boolean): number | null => {
      if (!active) {
        return null;
      }
      const idx = dataGridStore.orderBy.findIndex((field: string) =>
        field.match(new RegExp(`${column}|${column}-desc`))
      );

      return idx > -1 ? idx + 1 : null;
    };

    const setLastGroupContent = (value?: string) => {
      lastGroupContent = value || "";
    };

    const groupHeader = () => {
      const isGroupingHeader = dataGridStore.columnDefinitions.some(
        (column: ColumnDefinition) => column.group
      );

      if (!isGroupingHeader) return "";

      const group: GroupColumnDefinition[] = [];
      let groupPrevius = undefined;
      let qtd = 0;

      for (const column of dataGridStore.columnDefinitions) {
        const groupColumn = column.group || "";

        if (groupPrevius === undefined) {
          qtd = (props.checkable ? 2 : 1);
          groupPrevius = groupColumn;
          continue;
        }

        if (groupPrevius !== groupColumn) {
          group.push({
            headerName: groupPrevius,
            colSpan: qtd,
          });

          groupPrevius = groupColumn;
          qtd = 0;
        }

        qtd++;
      }

      // Last
      group.push({
        headerName: groupPrevius || "",
        colSpan: qtd,
      });

      return (
        <TableRow>
          {group.map((groupColumn: GroupColumnDefinition, i: number) => {
            let style = {};

            if (i !== 0) {
              style = {
                borderLeftStyle: "ridge",
                borderLeftColor: "rgba(224, 224, 224, 1)",
                borderLeftWidth: "1px",
              };
            }

            return (
              <TableCell
                key={`cell-${i}`}
                align="center"
                colSpan={groupColumn.colSpan}
                style={style}
              >
                <b>{groupColumn.headerName}</b>
              </TableCell>
            );
          })}
        </TableRow>
      );
    };

    const handleColumnSorter = (column: string): void => {
      let sorter = dataGridStore.orderBy.find((field: string) =>
        field.match(new RegExp(`${column}|${column}-desc`))
      );

      let orderByList = [...dataGridStore.orderBy];
      let idx: any = orderPriority(column, true);

      switch (sorter) {
        case undefined:
          sorter = `${column}`;
          orderByList.push(sorter);
          break;
        case `${column}`:
          sorter = `${sorter}-desc`;
          if (idx) {
            orderByList[idx - 1] = sorter;
          }
          break;
        case `${column}-desc`:
          sorter = undefined;
          orderByList.splice(idx - 1, 1);
          break;
      }

      dataGridStore.setOrderBy([]);
      dataGridStore.setOrderBy([...orderByList]);
      dataGridStore.list();
    };

    const buildAdvancedFilterButton = () => {
      return (
        <FlatButton
          endIcon={
            <Icon>
              {dataGridStore.expand ? "arrow_drop_up" : "arrow_drop_down"}
            </Icon>
          }
          text={"Filtro avançado"}
          onClick={() => {
            dataGridStore.setExpand(!dataGridStore.expand);
          }}
        />
      );
    };

    const searchInput: InputTextParameters = {
      label: `${dataGridStore.defaultFilterLabels !== ""
        ? "Pesquise por " + dataGridStore.defaultFilterLabels
        : ""
        }`,
      fullWidth: true,
      defaultValue: "",
      value: defaultFilter,
      onKeyDown: (event: any) => {
        if (event.keyCode === 13) {
          const value = dataGridStore.inputSearchValue;

          const _filter: Filter[] = [...dataGridStore.filter];

          const idxDefault = _filter.findIndex((f) => {
            return f.isDefaultSearch;
          });

          if (idxDefault > -1) {
            _filter[idxDefault].value = value;
          }

          dataGridStore.setFilter(_filter);

          dataGridStore.list();
        }
      },
      onChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => {
        setDefaultFilter(event.target.value);
        dataGridStore.setInputSearchValue(event.target.value);
        const _filter: Filter[] = [...dataGridStore.filter];
        if (_filter.length > 0 && _filter[0].isDefaultSearch) {
          _filter[0].value = defaultFilter;
        }
        dataGridStore.setFilter(_filter)
      },
      sufix: (
        <IconButton
          size={"small"}
          onClick={() => {
            setDefaultFilter("");
            dataGridStore.setInputSearchValue("");

            const _filter = dataGridStore.filter;

            const idx: number = _filter.findIndex((f) => {
              return f.isDefaultSearch;
            });

            if (idx > -1) {
              _filter[idx].value = null;
            }

            dataGridStore.setFilter(_filter);
          }}
        >
          <Icon>delete</Icon>
        </IconButton>
      ),
    };

    const renderGroupingLocal = (row: any, index: number, list: any[]): React.ReactNode => {
      if (!props.renderGrouping) {
        return null;
      }

      return props.renderGrouping(row, index, list);
    }

    return (
      <div
        className={`container data-grid data-grid-${props.title
          .toLowerCase()
          .replace(/[^A-Z0-9\-]/, "-")}`}
      >
        <div className="row filters X--middle">
          {!props.disablefilterDefault && <div className="X12 M4">
            <InputText {...searchInput} />
          </div>}
          {props.filters ? props.filters : ""}
          {props.advancedFilters ? (
            <div className="X12">
              <Collapse in={dataGridStore.expand} timeout="auto" unmountOnExit>
                {props.advancedFilters}
              </Collapse>
            </div>
          ) : (
            ""
          )}
          {props.checkable && props.renderCheckedActions ? (
            <div className="X6" style={{ textAlign: "left" }}>
              {props.renderCheckedActions()}
            </div>
          ) : ""}
          <div className={props.checkable ? "X6" : "X12"} style={{ textAlign: "right" }}>
            {props.advancedFilters ? buildAdvancedFilterButton() : ""}
            <FlatButton
              color={"primary"}
              text={"Pesquisar"}
              variant={"contained"}
              onClick={() => {
                const value = dataGridStore.inputSearchValue;

                const _filter: Filter[] = dataGridStore.filter;

                const idxDefault = _filter.findIndex((f) => {
                  return f.isDefaultSearch;
                });

                if (idxDefault > -1) {
                  _filter[idxDefault].value = value;
                }

                dataGridStore.setFilter(_filter);

                dataGridStore.list();
              }}
            />
            <IconButton
              ref={anchorRef}
              aria-controls={open ? "menu-list-grow" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
            >
              <Icon>more_vert</Icon>
            </IconButton>
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              role={undefined}
              transition
              disablePortal
              style={{ zIndex: 1650 }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === "bottom" ? "center top" : "center bottom",
                  }}
                >
                  <Paper>
                    <ClickAwayListener onClickAway={handleClose}>
                      <MenuList
                        autoFocusItem={open}
                        id="menu-list-grow"
                        onKeyDown={handleListKeyDown}
                      >
                        <MenuItem
                          onClick={handleReport}
                          disabled={dataGridStore.enableExport === false}
                        >
                          <ListItemIcon>
                            <FontAwesomeIcon icon={faFileExcel} />
                          </ListItemIcon>
                          <Typography variant="inherit" noWrap>
                            Exportar planilha
                          </Typography>
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
            {props?.renderAggregation
              ? <>
                <Tooltip title="Mais informações">
                  <IconButton size="small" onClick={() => {
                    setOpenAgg(!openAgg)
                  }}>
                    <Icon>{openAgg ? "arrow_drop_up" : "arrow_drop_down"}</Icon>
                  </IconButton>
                </Tooltip>
              </>
              : ""
            }
          </div>
        </div>

        {props?.renderAggregation ?
          <>
            <Collapse in={openAgg} style={{ marginBottom: "16px" }}>
              {props?.renderAggregation(dataGridStore.aggregations)}
            </Collapse>
          </>
          : ""
        }
        <div className="row">
          <div className="X12">
            <Paper elevation={0} id="grid-container">
              <TableContainer

                style={{
                  width: props.width,
                  maxHeight: props.maxHeight,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    {groupHeader()}
                    <TableRow>
                      {props?.checkable ? (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={dataGridStore.checkedAll}
                            indeterminate={dataGridStore.isIndeterminate()}
                            onChange={(event: any) => {
                              if (props.checkField) {
                                dataGridStore.toggleCheckAll(props.checkField)
                                // callback
                                if (props.onCheckAll) {
                                  props.onCheckAll()
                                }
                              }
                            }}

                          />
                        </TableCell>
                      ) : ""}

                      {dataGridStore.columnDefinitions.map(
                        (columnDef: ColumnDefinition, i: number) => {
                          let style = {};

                          style = {
                            paddingTop: "20px",
                            top: isGroupHeader ? 37 : 0,
                          };

                          if (i !== 0 && isGroupHeader) {
                            if (lastGroup !== columnDef.group) {
                              style = {
                                paddingTop: "20px",
                                top: isGroupHeader ? 37 : 0,
                                borderLeftStyle: "ridge",
                                borderLeftColor: "rgba(224, 224, 224, 1)",
                                borderLeftWidth: "1px",
                              };
                            }
                          }

                          lastGroup = columnDef.group;

                          return (
                            <TableCell key={i} style={style} align={Formater.toAlign(columnDef?.type, columnDef?.align)}>
                              <TableSortLabel
                                IconComponent={ArrowDropDown}
                                disabled={!columnDef.sortable}
                                active={isOrderActive(
                                  columnDef.field,
                                  columnDef.sortable
                                    ? columnDef.sortable
                                    : false
                                )}
                                direction={
                                  dataGridStore.orderBy.includes(
                                    `${columnDef.field}-desc`
                                  )
                                    ? "desc"
                                    : "asc"
                                }
                                onClick={() =>
                                  handleColumnSorter(columnDef.field)
                                }
                              >
                                <Badge
                                  color={"primary"}
                                  anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                  }}
                                  badgeContent={orderPriority(
                                    columnDef.field,
                                    columnDef.sortable
                                      ? columnDef.sortable
                                      : false
                                  )}
                                >
                                  {!columnDef.description ? (
                                    <span>{columnDef.headerName}</span>
                                  ) : (
                                    <Tooltip
                                      title={columnDef.description}
                                      placement="bottom"
                                    >
                                      <span>{columnDef.headerName}</span>
                                    </Tooltip>
                                  )}
                                </Badge>
                              </TableSortLabel>
                            </TableCell>
                          );
                        }
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dataGridStore?.rows?.length > 0
                      ? dataGridStore.rows.map((row: any, idx: number) => {
                        const key: string = `componentes-row-${row.id}-${idx}`;
                        if (props.expandableRows) {
                          return (
                            <ExpandableRow
                              key={key}
                              row={row}
                              columnDefinitions={
                                dataGridStore.columnDefinitions
                              }
                              renderExpandable={props.renderExpandable}
                              checkable={props.checkable}
                              isChecked={!!props.checkField && dataGridStore.rowIsChecked(row, props.checkField)}
                              onCheck={(row) => {
                                if (props.checkField) {
                                  dataGridStore.toggleRowCheck(row, props.checkField)
                                }
                              }}
                            />
                          );
                        }

                        return (
                          <>
                            {renderGroupingLocal(row, idx, dataGridStore.rows)}
                            <Row
                              key={key}
                              columnDefinitions={
                                dataGridStore.columnDefinitions
                              }
                              isGroupHeader={isGroupHeader}
                              lastGroupContent={lastGroupContent}
                              setLastGroupContent={setLastGroupContent}
                              row={row}
                              checkable={props.checkable}
                              isChecked={!!props.checkField && dataGridStore.rowIsChecked(row, props.checkField)}
                              onCheck={(row) => {
                                if (props.checkField) {
                                  dataGridStore.toggleRowCheck(row, props.checkField)
                                }
                              }}
                            />
                          </>
                        );
                      })
                      : ""}
                  </TableBody>
                </Table>
              </TableContainer>

            </Paper>
          </div>
        </div>
        {dataGridStore.pagination &&
          <BottomBar expanded={false}>
            <TablePagination
              variant="footer"
              style={{ width: "100%" }}
              labelRowsPerPage={"Registros por página:"}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count} registros`
              }
              page={dataGridStore.page - 1}
              rowsPerPageOptions={dataGridStore.rowsPerPageOptions}
              count={dataGridStore.totalRows}
              rowsPerPage={dataGridStore.pageSize}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onPageChange={(
                event: React.MouseEvent<HTMLButtonElement> | null,
                newPage: number
              ) => {
                handleChangePage(newPage);
              }}
              onRowsPerPageChange={(
                event: React.ChangeEvent<
                  HTMLInputElement | HTMLTextAreaElement
                >
              ) => {
                handleChangeRowsPerPage(event.target.value);
              }}
            />
          </BottomBar>
        }
      </div>
    );
  });

export { DataGrid };
